import {
  IPrescriptionsOrders,
  IPresTruncatedDataDTO,
  MEDICINE_STATUSES,
} from 'API';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary } from 'Hooks';
import { useEffect, useState } from 'react';
import { AccordionSection } from './AccordionSection';
import { addMonths } from 'date-fns';

interface IPrescriptionsListProps {
  titles: string[];
  items: IPrescriptionsOrders;
  onShowPrescriptionDetailsClick: (
    clickedPrescription: IPresTruncatedDataDTO
  ) => void;
}

export const PrescriptionsList = (props: IPrescriptionsListProps) => {
  const [defaultExpandedAccordion, setDefaultExpandedAccordion] = useState<
    'futureOrders' | 'inProgressOrders' | 'pastOrders' | null
  >(null);

  const allOrders = props.items.prescriptions;

  allOrders &&
    allOrders.sort(
      (a, b) =>
        new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
    );

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby
  );

  const futureOrders = allOrders.filter((order) => {
    if (order.createDate) {
      const orderDate = new Date(order.createDate);
      return orderDate > new Date();
    } else return false;
  });

  const inProgressOrders = allOrders.filter(
    (order) =>
      order.currentStatus !== MEDICINE_STATUSES.SUPERPHARM_FULL_SUPPLY &&
      order.currentStatus !== MEDICINE_STATUSES.REJECTED &&
      new Date(order.createDate) < new Date()
  );

  const pastOrders = allOrders.filter(
    (order) =>
      order.currentStatus === MEDICINE_STATUSES.SUPERPHARM_FULL_SUPPLY ||
      order.currentStatus === MEDICINE_STATUSES.REJECTED
  );

  useEffect(() => {
    if (inProgressOrders.length > 0) {
      setDefaultExpandedAccordion('inProgressOrders');
    } else if (futureOrders.length > 0) {
      setDefaultExpandedAccordion('futureOrders');
    } else if (pastOrders.length > 0) {
      setDefaultExpandedAccordion('pastOrders');
    } else {
      setDefaultExpandedAccordion(null);
    }
  }, [
    allOrders,
    futureOrders.length,
    inProgressOrders.length,
    pastOrders.length,
  ]);

  return (
    <>
      {futureOrders.length > 0 && (
        <AccordionSection
          titles={props.titles}
          items={futureOrders}
          defaultExpandedAccordion={defaultExpandedAccordion === 'futureOrders'}
          onShowPrescriptionDetailsClick={props.onShowPrescriptionDetailsClick}
          title={dictionary.futureOrdersAccordionTitle}
          subTitle={dictionary.futureOrdersAccordionSubTitle}
        />
      )}

      <AccordionSection
        titles={props.titles}
        items={inProgressOrders}
        defaultExpandedAccordion={
          defaultExpandedAccordion === 'inProgressOrders'
        }
        onShowPrescriptionDetailsClick={props.onShowPrescriptionDetailsClick}
        title={dictionary.inProgressOrdersAccordionTitle}
        subTitle={
          inProgressOrders.length === 0
            ? dictionary.noOrdersFound
            : dictionary.inProgressOrdersAccordionSubTitle
        }
        isDisabled={inProgressOrders.length === 0}
      />
      <AccordionSection
        titles={props.titles}
        items={pastOrders}
        defaultExpandedAccordion={defaultExpandedAccordion === 'pastOrders'}
        onShowPrescriptionDetailsClick={props.onShowPrescriptionDetailsClick}
        title={dictionary.pastOrdersAccordionTitle}
        subTitle={dictionary.pastOrdersAccordionSubTitle}
        isDisabled={pastOrders.length === 0}
      />
    </>
  );
};
